import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import isEqual from 'react-fast-compare';
import {
  EventAccordionItemProps,
  translateEventToSaleAccordionItem,
} from 'src/components/Accordions';
import { SwiperButton } from 'src/components/Buttons/SwiperButton';
import { BackSection } from 'src/components/common/BackSection';
import * as EmptySectionContent from 'src/components/common/EmptySectionContent';
import {
  EventPageTabs,
  TabItem,
} from 'src/components/Events/EventPage/EventPageTabs';
import { SidePanel } from 'src/components/Events/EventPage/SidePanel/SidePanel';
import { TagsSection } from 'src/components/Listings/InventoryEventPage/Sections/TagsSection';
import { ActiveFocusContextProvider } from 'src/contexts/ActiveFocusContext/ActiveFocusContext';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { CollapsableViewProvider } from 'src/contexts/CollapsableViewContext/CollapsableViewContext';
import { Content } from 'src/contexts/ContentContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import {
  SidePanelPage,
  SidePanelProvider,
} from 'src/contexts/SidePanelContext';
import { ExplorerLayout } from 'src/core/layouts';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useQuickFilterSearchParam } from 'src/hooks/useQuickFilters';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { TagNewIcon } from 'src/svgs';
import { SaleIcon } from 'src/svgs/SaleIcon';
import { SearchSolidIcon } from 'src/svgs/Viagogo';
import { SaleDeeplinkQueryParam } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { getDeepLinkIdFromUrl } from 'src/utils/deepLinkUtils';
import { getAppliedFilterCounts } from 'src/utils/eventQueryUtils';
import { getSaleDetailsModalConfigWithDeepLink } from 'src/utils/saleUtils';
import {
  EventWithData,
  Feature,
  Sale,
  SaleDetails,
  SaleQuery,
  UserSetting,
} from 'src/WebApiController';
import Swiper from 'swiper';

import * as styles from '../../Events/EventPage/EventPage.css';
import { SaleEventInfoSection } from './SaleEventInfoSection';
import { SaleEventPageHeader } from './SaleEventPageHeader';
import { SalesEventPageHeaderV2 } from './SalesEventPageHeaderV2/SalesEventPageHeaderV2';

export function SaleEventPage({
  viagVirtId,
  posEventIds,
  returnUrl,
}: {
  posEventIds?: string[];
  viagVirtId?: string;
  returnUrl?: string;
}) {
  const { activeAccountWebClientConfig } = useAppContext();
  const {
    eventsTransformed,
    eventsExpansion: { setListItemExpansion },
  } = useCatalogDataContext();

  useEffect(() => {
    if (!viagVirtId && posEventIds?.length) {
      setListItemExpansion(
        true,
        posEventIds.map((id) => `old:${id}`)
      );
    } else if (viagVirtId) {
      setListItemExpansion(true, [viagVirtId]);
    }
  }, [
    activeAccountWebClientConfig,
    posEventIds,
    setListItemExpansion,
    viagVirtId,
  ]);

  const { allGroupsIds, setAllGroupsIds } = useMultiSelectionContext();

  const accordionItemProps = useMemo(
    () =>
      eventsTransformed?.length
        ? eventsTransformed.find(
            (ev) =>
              ev.event.virtId === viagVirtId ||
              ev.event.posIds.some((id) => posEventIds?.includes(id))
          )
        : undefined,
    [eventsTransformed, posEventIds, viagVirtId]
  );

  const salesAccordionItem = useMemo(
    () =>
      accordionItemProps &&
      translateEventToSaleAccordionItem(accordionItemProps, 0, true),
    [accordionItemProps]
  );

  useEffect(() => {
    // Important useEffect to keep allGroupIds in sync with the items
    // This is important for the multi select to work properly
    if (
      salesAccordionItem &&
      !isEqual(allGroupsIds, [salesAccordionItem.itemId])
    ) {
      setAllGroupsIds([salesAccordionItem.itemId]);
    }
  }, [allGroupsIds, salesAccordionItem, setAllGroupsIds]);

  return (
    <SidePanelProvider sidePanelId="sale-event">
      <ActiveFocusContextProvider>
        <Stack direction="column" height="full">
          <SaleEventContent
            accordionItemProps={accordionItemProps}
            saleAccordionItem={salesAccordionItem}
            returnUrl={returnUrl}
          />
        </Stack>
      </ActiveFocusContextProvider>
    </SidePanelProvider>
  );
}

type SalesEventContentProps = {
  accordionItemProps?: EventWithData;
  saleAccordionItem?: EventAccordionItemProps<Sale>;
  returnUrl?: string;
};

export const enum SaleEventTabs {
  Sales = 'sales',
  Tags = 'tags',
  None = 'none',
}

function SaleEventContent({
  accordionItemProps,
  saleAccordionItem,
  returnUrl,
}: SalesEventContentProps) {
  const intialActiveTab = SaleEventTabs.Sales;

  const [selectedSaleIds, setSelectedSaleIds] = useState<number[]>([]);
  const [activeTab, setActiveTab] = useState(intialActiveTab);

  const { isLoading } = useCatalogDataContext();
  const isMobile = useMatchMedia('mobile');

  const quickFilterSearchParams = useQuickFilterSearchParam();
  const { tempQuery, initialQuery, filterQuery } =
    useFilterQueryContext<SaleQuery>();

  const { setModal } = useContext(ModalContext);
  const { posEntity: sale, setActivePosEntity } =
    useActivePosEntityContext<SaleDetails>();

  const {
    value: defaultSalesTabUserSetting,
    setUserSetting: setDefaultSalesTabUserSetting,
    isLoading: defaultSalesTabLoading,
  } = useServerUserSetting<SaleEventTabs>({
    id: UserSetting.SalesDefaultTab,
  });

  const hasEventHeaderReworkSalesFeature = useUserHasFeature(
    Feature.EventHeaderRework_Sales
  );

  const hasStandaloneEntityDetailModalsFeature = useUserHasFeature(
    Feature.StandaloneEntityDetailModals
  );

  const activeId = getDeepLinkIdFromUrl(
    SaleDeeplinkQueryParam,
    window.location.href
  );

  useEffect(() => {
    if (activeId?.length && !sale) {
      setActivePosEntity(parseInt(activeId), undefined, true);
      setModal(getSaleDetailsModalConfigWithDeepLink(activeId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const [defaultTab, setDefaultTab] = useState<SaleEventTabs | undefined>(
    defaultSalesTabUserSetting
  );

  useEffect(() => {
    if (
      defaultSalesTabUserSetting != null &&
      defaultSalesTabUserSetting !== activeTab &&
      defaultSalesTabUserSetting !== SaleEventTabs.None &&
      defaultSalesTabLoading === false
    ) {
      setActiveTab(defaultSalesTabUserSetting);
    }
    if (
      defaultSalesTabUserSetting != null &&
      defaultSalesTabUserSetting !== defaultTab &&
      defaultSalesTabUserSetting !== SaleEventTabs.None &&
      defaultSalesTabLoading === false
    ) {
      setDefaultTab(defaultSalesTabUserSetting);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSalesTabLoading, defaultSalesTabUserSetting]);

  const onSetDefaultTabClick = useCallback(
    async (tab: SaleEventTabs) => {
      if (tab !== activeTab && tab !== SaleEventTabs.None) {
        setActiveTab(tab);
      }
      if (tab !== defaultTab) {
        setDefaultTab(tab);
      }
      setDefaultSalesTabUserSetting(tab);
    },
    [activeTab, defaultTab, setDefaultSalesTabUserSetting]
  );

  const swiperRef = useRef<Swiper>();

  const event = accordionItemProps?.event;

  const hasConsignorMasterUI = useUserHasConsignorMasterUI();

  const tabs: TabItem<SaleEventTabs>[] = [
    {
      value: SaleEventTabs.Sales,
      label: <Content id={ContentId.Sales} />,
      content: (
        <SaleEventInfoSection
          accordionItemProps={accordionItemProps}
          saleAccordionItem={saleAccordionItem}
          selectedSaleIds={selectedSaleIds}
          setSelectedSaleIds={setSelectedSaleIds}
        />
      ),
      className: styles.tabContent,
      Icon: SaleIcon,
      contentId: ContentId.Sales,
      useStroke: true,
    },
  ];

  if (!hasConsignorMasterUI) {
    tabs.push({
      value: SaleEventTabs.Tags,
      label: <Content id={ContentId.Tags} />,
      content: event?.virtId && <TagsSection eventId={event?.virtId} />,
      Icon: TagNewIcon,
      contentId: ContentId.Tags,
      useStroke: true,
    });
  }

  if (!event) {
    if (
      quickFilterSearchParams ||
      getAppliedFilterCounts(tempQuery, initialQuery)
    )
      return (
        <EmptySectionContent.Root
          icon={
            <EmptySectionContent.SolidIconContainer>
              <SearchSolidIcon />
            </EmptySectionContent.SolidIconContainer>
          }
        >
          <EmptySectionContent.Label>
            <Content id={ContentId.NoResultFound} />
          </EmptySectionContent.Label>
          <EmptySectionContent.DetailMessage>
            <Content id={ContentId.NoFilteredNotifications} />
          </EmptySectionContent.DetailMessage>
        </EmptySectionContent.Root>
      );
  }

  if (isLoading || !event) {
    return <PosSpinner />;
  }

  return (
    <div className={styles.root}>
      <ExplorerLayout
        swiperRef={swiperRef}
        defaultRatio={0.35}
        maxPanelPercent={0.5}
        showExpandButton
        sidePanelPage={SidePanelPage.SaleEvent}
        rightPanelContent={
          <SidePanel
            event={event}
            pageType="Sales"
            selectedEntityIds={selectedSaleIds}
            filterQuery={filterQuery}
          />
        }
      >
        <div className={styles.leftContainer}>
          {isMobile && !hasEventHeaderReworkSalesFeature && (
            <Stack
              justifyContent="spaceBetween"
              alignItems="center"
              style={{
                padding: `0 ${vars.spacing['xl']} ${vars.spacing['lg']} ${vars.spacing['xl']}`,
              }}
            >
              <BackSection
                returnUrl={returnUrl ?? '/sales'}
                returnState={{
                  fromViagVirtId: event.virtId,
                }}
              />
              <SwiperButton dir="right" />
            </Stack>
          )}
          <CollapsableViewProvider isEnabled={true}>
            {hasEventHeaderReworkSalesFeature ? (
              <SalesEventPageHeaderV2
                event={event}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                tabs={tabs}
                defaultTab={defaultTab}
                onUpdateDefaultTab={onSetDefaultTabClick}
              />
            ) : (
              <>
                <SaleEventPageHeader
                  event={event}
                  saleAccordionItem={saleAccordionItem}
                />
                <EventPageTabs
                  tabs={tabs}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  onSetDefaultTabClick={onSetDefaultTabClick}
                />
              </>
            )}
          </CollapsableViewProvider>
        </div>
      </ExplorerLayout>
    </div>
  );
}
